import { AppBar, Toolbar, Typography, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Icon } from '@liminal/web-ui/icons/Icon';
import { StatusPill, type StatusPillTone } from '@liminal/web-ui/primitives/StatusPill';
import { ADMIN_ENV, type AdminEnv } from '@/lib/env';
import { fontWeights } from '@liminal/brand/scale';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const MenuButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(1.5),
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: fontWeights.semibold,
  color: theme.palette.text.primary,
}));

const EnvPillSlot = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  marginLeft: theme.spacing(1.5),
}));

// The consoles are otherwise pixel-identical, which is how a test suspension
// lands on a real user. Every console but prod's is marked, and loudly; the
// prod console looks the way it always has.
const ENV_PILL: Record<AdminEnv, { label: string; tone: StatusPillTone } | null> = {
  dev: { label: 'DEV', tone: 'warning' },
  testnet: { label: 'TESTNET', tone: 'warning' },
  prod: null,
};

interface HeaderProps {
  title: string;
  onMenuClick: () => void;
}

export function Header({ title, onMenuClick }: HeaderProps) {
  const envPill = ENV_PILL[ADMIN_ENV];

  return (
    <StyledAppBar position="static" color="default" elevation={0}>
      <Toolbar>
        <MenuButton edge="start" aria-label="Open menu" onClick={onMenuClick}>
          <Icon name="menu" size={24} />
        </MenuButton>
        <Title variant="h5">
          {title}
        </Title>
        {envPill && (
          <EnvPillSlot>
            <StatusPill label={envPill.label} tone={envPill.tone} size="sm" data-testid="env-pill" />
          </EnvPillSlot>
        )}
      </Toolbar>
    </StyledAppBar>
  );
}
